/**
 * @file Renders the investment's transaction table and funding description list.
 */
import { useMemo, useRef } from "react";

import Collection from "@components/Collection";

import getTableSummary from "./helpers/getTableSummary";
import getColumnsConfig from "./helpers/getColumnsConfig";
import FundingDescriptions from "./FundingDescriptions";
import useTransactionOperations from "./helpers/useTransactionOperations";
import type { EditableTransaction } from "./helpers/types";
import type { Investment, ReadInvestment, CollectionHandle } from "../../types";

const DEFAULT_EDITABLE_TRANSACTION: EditableTransaction = {
  id: null,
  date: "",
  amount: 0,
  referenceId: null,
  editableRow: true,
};

type Props = {
  investment: Investment;
  readInvestment: ReadInvestment;
  isInvestmentFundedTemplateEnabled: () => boolean;
};

/**
 * Renders the investment's editable transaction table and its funding descriptions.
 */
const InvestmentFunding = ({
  investment,
  readInvestment,
  isInvestmentFundedTemplateEnabled,
}: Props) => {
  const collection = useRef<CollectionHandle>({});

  const {
    id: investmentId,
    transactions: investmentTransactions = [],
  } = investment;

  const transactions = useMemo((): EditableTransaction[] =>
    [
      ...(
        investmentTransactions
          .map((transaction) => ({ ...transaction, editableRow: false }))
      ),
      { ...DEFAULT_EDITABLE_TRANSACTION },
    ]
  , [ investmentTransactions ]);

  const {
    createTransaction,
    deleteTransaction,
  } = useTransactionOperations(collection, investmentId, readInvestment);

  const summary = getTableSummary();
  const columnsConfig = getColumnsConfig({
    collection,
    createTransaction,
    deleteTransaction,
    isInvestmentFundedTemplateEnabled,
  });

  const tableProps = {
    size: "small",
    rowKey: "id",
    bordered: false,
  };

  const items = transactions || [];
  const selectedRowItems = items.map(({ key }) => key);
  const enableSelectItem = false;

  const collectionProps = {
    items,
    summary,
    tableProps,
    collection,
    columnsConfig,
    enableSelectItem,
    selectedRowItems,
  };

  return (
    <>
      <Collection
        {...collectionProps}
      />

      <FundingDescriptions
        investment={investment}
        readInvestment={readInvestment}
      />
    </>
  );
};

export default InvestmentFunding;
